import { useState } from "react";
import { Info, PieChart, BarChart2, LineChart } from "lucide-react";
import BarGraph2 from "./BarGraph2";
import CustomTooltip2 from "./CustomTooltip2";
import TimeCompareDropdown from "../../StudentData/Dropdowns/TimeCompareDropdown";
import PieGraph from "./PieGraph";
import LineGraph from "./LineGraph";
import { getEmojiImage, emotionColors, barEmotionOrder } from "../utils";

const EmotionsDistribution = ({ state, updateState, emotionPercents, name }) => {
  const [activeChart, setActiveChart] = useState("pie");

  const BarGraphComponent = (props) => {
    const CustomTooltipWrapper = (props) => {
      return <CustomTooltip2 {...props} state={state} />;
    };
    return (
      <div>
        <div>
          <div className="flex items-center gap-3 mb-3 bg-white p-2 rounded justify-end w-full mt-5">
            <div className="flex items-center">
              <span className="text-400 text-[18px]">Compare To</span>
              <TimeCompareDropdown state={state} updateState={updateState} />
            </div>
          </div>
          <div className="w-auto h-[450px] m-auto pt-2 box-border">
            <BarGraph2
              state={state}
              barGraphWithPercent={emotionPercents}
              CustomTooltip={CustomTooltipWrapper}
            />
          </div>
        </div>
      </div>
    );
  };

  const PieGraphComponent = () => {
    return (
      <div>
        <div>
          <PieGraph
            pieGraphData={emotionPercents}
            tooltipCounts={state.tooltipCounts}
            chosenTime={state.chosenTime}
            chosenTimeCompare={state.chosenTimeCompare}
            studentEmailToNameMap={state.studentEmailToNameMap}
            chosenStudent={state.chosenStudent}
          />
        </div>
      </div>
    );
  };

  const LineGraphComponent = () => {
    return (
      <div className="w-auto h-[450px] m-auto pt-2 box-border">
        <LineGraph
          lineGraphData={state.lineGraphDerivedData}
          selectedEmotions={state.lineGraphSelectedEmotions}
          updateState={updateState}
          studentEmailToNameMap={state.studentEmailToNameMap}
        />
      </div>
    );
  };

  const renderChart = () => {
    switch (activeChart) {
      case "pie":
        return <PieGraphComponent />;
      case "bar":
        return <BarGraphComponent />;
      case "line":
        return <LineGraphComponent />;
      default:
        return (
          <div className="h-64 bg-gray-50 rounded-lg flex items-center justify-center">
            Select a chart type
          </div>
        );
    }
  };

  // Prepare ordered emotions for mobile list view
  const orderedEmotions = (barEmotionOrder || [])
    .map((emotion) => emotionPercents.find((e) => e.name === emotion))
    .filter(Boolean);

  return (
    <div className="flex-1 bg-white shadow p-3 sm:p-4 2xl:p-8 rounded">
      <div className="flex justify-between items-center mb-2 gap-2">
        <div className="flex items-center gap-2">
          <img
            src={"/images/emotions.png"}
            alt="emotions"
            className="w-4 h-5"
          />
          <h3 className="text-[22px] sm:text-[24px] font-bold">
          {name && state.chosenStudent && state.chosenStudent !== "All Students"
            ? `${name}'s Emotions Distribution`
              : "Emotions Distribution"}
          </h3>
          <div className="group relative inline-block">
            <Info className="w-4 h-4 text-400 text-[#5b9cd6] hover:text-gray-600 cursor-pointer transition-colors" />
            <div className="absolute hidden group-hover:block right-1/2 sm:right-0 2xl:left-0 mt-2 w-48 sm:w-64 p-3 text-sm bg-gray-100 rounded shadow z-50">
              <span className="text-400 text-[14px]">
                Hover on emotions to view trends and affected students. Click
                the emotion for the student count and the link to view their
                data.
              </span>
            </div>
          </div>
        </div>
        <div className="sm:flex space-x-2 hidden">
          <button
            className={`p-1 hover:bg-gray-100 border-b-2 ${
              activeChart === "pie"
                ? "border-black text-black"
                : "border-transparent text-gray-600"
            }`}
            onClick={() => setActiveChart("pie")}
            aria-label="Switch to Pie Chart"
          >
            <PieChart className="w-4 h-4" />
          </button>
          <button
            className={`p-1 hover:bg-gray-100 border-b-2 ${
              activeChart === "bar"
                ? "border-black text-black"
                : "border-transparent text-gray-600"
            }`}
            onClick={() => setActiveChart("bar")}
            aria-label="Switch to Bar Chart"
          >
            <BarChart2 className="w-4 h-4" />
          </button>
          <button
            className={`p-1 hover:bg-gray-100 border-b-2 ${
              activeChart === "line"
                ? "border-black text-black"
                : "border-transparent text-gray-600"
            }`}
            onClick={() => setActiveChart("line")}
            aria-label="Switch to Line Chart"
          >
            <LineChart className="w-4 h-4" />
          </button>
        </div>
      </div>
      {activeChart !== "line" &&
        (state.chosenStudent && state.chosenStudent !== "All Students" ? (
          <h4 className="text-400 text-[18px] ml-6">
            Discover which emotions are impacting this student.
          </h4>
        ) : (
          <h4 className="text-400 text-[18px] ml-6">
            Discover which emotions are impacting different students.
          </h4>
        ))}
      {/* Mobile-only stacked bar graph per emotion*/}
      <div className="sm:hidden mt-3 flex flex-col gap-3">
        {orderedEmotions.map((d) => {
          const percent =
            typeof d.currentPercent === "number" ? d.currentPercent : 0;
          const color = emotionColors[d.name] || "#999999";
          return (
            <div key={d.name} className="flex items-center gap-3 w-full">
              <div className="flex items-center justify-start shrink-0 gap-1 w-24">
                <img
                  src={getEmojiImage(d.name)}
                  alt={d.name}
                  className="w-6 h-6"
                  style={{ color }}
                />
                <span className="capitalize text-base shrink-0">{d.name}</span>
              </div>
              <div className="flex-1 h-6 w-28 bg-[#EEEEEE] rounded overflow-hidden">
                <div
                  className="h-full rounded"
                  style={{ width: `${percent}%`, backgroundColor: color }}
                />
              </div>
              <span className="text-right text-sm w-10 font-semibold">
                {percent}%
              </span>
            </div>
          );
        })}
      </div>
      <div className="hidden sm:block items-center justify-center">
        {renderChart()}
      </div>
    </div>
  );
};

export default EmotionsDistribution;
